/** @jsx jsx */
import { css, jsx } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import LinkIcon from '@atlaskit/icon/core/link';
import LinkIconOld from '@atlaskit/icon/core/migration/link';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { N0, R300, N400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import UnlinkButton from '../unlink-button/index.tsx';

export type Props = {
	isOverlapping: boolean;
	isFiltered: boolean;
	label: string;
	onUnlink?: (analyticsEvent: UIAnalyticsEvent) => void; // optional handler, controls visibility of the unlink button
	unlinkLabel?: string;
};

const getBackgroundColor = (isOverlapping: boolean) =>
	isOverlapping
		? token('color.background.danger.bold', R300)
		: token('color.background.neutral.bold', N400);

const Separator = ({ label, isOverlapping, isFiltered, unlinkLabel, onUnlink }: Props) =>
	isVisualRefreshEnabled() && fg('visual-refresh_drop_1') ? (
		<Flex xcss={containerStyles} testId="roadmap.timeline-table-kit.common.ui.dependency.separator">
			<Flex xcss={separatorContainerStyles} direction="column" alignItems="center">
				<Box
					xcss={lineStyles}
					style={{
						border: `0.5px ${isFiltered ? 'dashed' : 'solid'} ${
							isOverlapping ? token('color.icon.danger') : token('color.icon')
						}`,
					}}
				/>
				<Box
					xcss={iconWrapperStyles}
					style={{
						backgroundColor: isOverlapping
							? token('color.background.accent.red.subtlest')
							: token('color.background.neutral'),
						border: `1px solid ${isOverlapping ? token('color.icon.danger') : token('color.icon')}`,
					}}
				>
					<LinkIcon
						label={fg('jsw_roadmaps_timeline-post-project-a11y-fix') ? '' : label}
						color={isOverlapping ? token('color.icon.danger') : token('color.icon')}
					/>
				</Box>
				<Box
					xcss={lineStyles}
					style={{
						border: `0.5px ${isFiltered ? 'dashed' : 'solid'} ${
							isOverlapping ? token('color.icon.danger') : token('color.icon')
						}`,
					}}
				/>
			</Flex>
			<Flex xcss={textContainerStyles} alignItems="center">
				<Text size="UNSAFE_small" weight="semibold">
					{label}
				</Text>
			</Flex>
			{onUnlink && (
				<Box xcss={unlinkButtonWrapperStyles}>
					<UnlinkButton
						onClick={onUnlink}
						{...(fg('jsw_roadmaps_timeline-post-project-a11y-fix') ? { label: unlinkLabel } : {})}
					/>
				</Box>
			)}
		</Flex>
	) : (
		<div
			css={containerStylesOld}
			data-testid="roadmap.timeline-table-kit.common.ui.dependency.separator"
		>
			<div css={separatorContainerStylesOld}>
				<div
					css={lineStylesOld}
					style={{
						border: `1px ${isFiltered ? 'dashed' : 'solid'} ${getBackgroundColor(isOverlapping)}`,
					}}
				/>
				<div
					css={iconWrapperStylesOld}
					style={{
						backgroundColor: getBackgroundColor(isOverlapping),
					}}
				>
					<LinkIconOld
						LEGACY_size="small"
						label={fg('jsw_roadmaps_timeline-post-project-a11y-fix') ? '' : label}
						color={token('color.icon.inverse', N0)}
					/>
				</div>
				<div
					css={lineStylesOld}
					style={{
						border: `1px ${isFiltered ? 'dashed' : 'solid'} ${getBackgroundColor(isOverlapping)}`,
					}}
				/>
			</div>

			<Flex xcss={textContainerStyles} alignItems="center">
				<Text size="UNSAFE_small" weight="semibold">
					{label}
				</Text>
			</Flex>
			{onUnlink && (
				<div css={unlinkButtonWrapperStylesOld}>
					<UnlinkButton
						onClick={onUnlink}
						{...(fg('jsw_roadmaps_timeline-post-project-a11y-fix') ? { label: unlinkLabel } : {})}
					/>
				</div>
			)}
		</div>
	);

export default Separator;

const containerStylesOld = css({
	display: 'flex',
	flexDirection: 'row',
	position: 'relative',
});

const containerStyles = xcss({
	position: 'relative',
});

const separatorContainerStylesOld = css({
	display: 'flex',
	flexDirection: 'column',
	marginLeft: token('space.200', '16px'),
	marginRight: token('space.100', '8px'),
	alignItems: 'center',
});

const separatorContainerStyles = xcss({
	marginLeft: 'space.100',
	marginRight: 'space.100',
});

const textContainerStyles = xcss({
	flexGrow: 1,
});

const iconWrapperStylesOld = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: token('space.250', '20px'),
	height: token('space.250', '20px'),
	boxShadow: token('elevation.shadow.raised', '1px 2px 3px 0 rgba(9, 30, 66, 0.13)'),
	borderRadius: '50%',
});

const iconWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '24px',
	height: '24px',
	borderRadius: '50%',
});

const lineStylesOld = css({
	height: token('space.150', '12px'),
});

const lineStyles = xcss({
	height: '12px',
});

const unlinkButtonWrapperStylesOld = css({
	position: 'absolute',
	top: token('space.150', '12px'),
	right: 0,
});

const unlinkButtonWrapperStyles = xcss({
	position: 'absolute',
	top: 'space.150',
	right: 'space.0',
});
